import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import {
  CommonFormElementSize,
  FloatingStrategy,
  PopupPlacement,
} from '@/common/types'
import { getValidityStateObject } from '@/common/utils'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { t } from '@/localization/localization'

import DapDSCalendar from '../calendar/calendar.component'
import DapDSFeedback from '../feedback/feedback.component'
import DapDSFormLabel from '../form/form-label/form-label.component'
import DapDSIcon from '../icon/icon.component'
import DapDSIconButton from '../icon-button/icon-button.component'
import BusinessCalendarLine from '../icons/business/business-calendar-line/business-calendar-line.component'
import SystemLoadingSpinner from '../icons/system/system-loading-spinner/system-loading-spinner.component'
import DapDSPopup from '../popup/popup.component'
import DapDSTooltip from '../tooltip/tooltip.component'
import DapDSTypography from '../typography/typography.component'
import styles from './datepicker.scss?inline'

interface MaskToken {
  type: 'digit' | 'separator'
  length?: number
  value?: string
}

/**
 * `dap-ds-datepicker`
 * @summary A datepicker is a graphical user interface widget that allows the user to select a date from a calendar and/or time from a time range.
 * @element dap-ds-datepicker
 * @title - Datepicker
 *
 * @property {Dayjs} value - The value of the datepicker.
 * @property {string} label - The label of the datepicker.
 * @property {boolean} hideLabel - Visually hides the label while keeping it available to assistive technology. (default: false)
 * @property {string} description - The description of the datepicker.
 * @property {'xs' | 'sm' | 'sm'} size - The size of the datepicker.
 * @property {boolean} disabled - The disabled state of the datepicker.
 * @property {boolean} required - The required state of the datepicker.
 * @property {boolean} readonly - The readonly state of the datepicker.
 * @property {boolean} autofocus - The autofocus state of the datepicker.
 * @property {string} tooltip - The tooltip of the datepicker.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the datepicker.
 * @property {string} feedback - The feedback of the datepicker.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the datepicker.
 * @property {boolean} optional - The optional state of the datepicker.
 * @property {string} optionalLabel - The optional label of the datepicker.
 * @property {string} requiredLabel - The required indicator of the datepicker. (default: '*')
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {string} autocomplete - The autocomplete of the datepicker.
 * @property {boolean} autofocus - The autofocus of the datepicker.
 *
 * @event {{ value: string }} dds-change - Fired when the datepicker value changes.
 * @event {{ value: string }} dds-input - Fired when the datepicker input value changes.
 * @event {{ value: string }} dds-valid-date - Fired when the datepicker input value is valid. Happens on manual input typing.
 * @event {{ value: string, type: 'invalid' | 'out-of-range' }} dds-invalid-date - Fired when the datepicker input value is invalid. Happens on manual input typing.
 * @event {{ void }} dds-clear - Fired when the datepicker is cleared.
 * @event {{ void }} dds-focus - Emitted when the datepicker gains focus.
 * @event {{ void }} dds-blur - Emitted when the datepicker loses focus.
 *
 * @csspart base - The main datepicker container.
 * @csspart calendar - The calendar of the datepicker.
 * @csspart trigger - The trigger button of the datepicker.
 * @csspart label - The label of the datepicker.
 * @csspart description - The description of the datepicker.
 * @csspart feedback - The feedback of the datepicker.
 * @csspart tooltip - The tooltip of the datepicker.
 * @csspart input - The input of the datepicker.
 * @csspart clear-button - The clear button of the datepicker.
 * @csspart calendar-grid-cell-base - The base of the calendar grid cell.
 * @csspart calendar-grid-cell - The cell of the calendar grid.
 * @csspart calendar-grid-header-cell-base - The base of the calendar grid header cell.
 * @csspart calendar-grid-header-cell - The header cell of the calendar grid.
 * @csspart calendar-grid-cell-today-mark - The today mark of the calendar grid cell.
 *
 * @cssproperty --dds-combobox-background - The background color of the combobox. (default: var(--dds-fields-background-default))
 * @cssproperty --dds-combobox-border-color - The border color of the combobox. (default: var(--dds-border-neutral-base))
 * @cssproperty --dds-combobox-border-width - The border width of the combobox. (default: var(--dds-border-width-base, 1px))
 * @cssproperty --dds-combobox-border-radius - The border radius of the combobox. (default: var(--dds-radius-base))
 * @cssproperty --dds-combobox-text-color - The text color of the combobox. (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-combobox-placeholder-color - The placeholder text color. (default: var(--dds-text-neutral-subtle))
 * @cssproperty --dds-combobox-disabled-background - The background color when disabled. (default: var(--dds-background-neutral-stronger))
 * @cssproperty --dds-combobox-disabled-text - The text color when disabled. (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-combobox-error-border - The border color for error state. (default: var(--dds-border-negative-base))
 * @cssproperty --dds-combobox-success-border - The border color for success state. (default: var(--dds-border-positive-base))
 * @cssproperty --dds-combobox-icon-color - The color of the icons. (default: var(--dds-text-icon-neutral-subtle))
 * @cssproperty --dds-combobox-clear-icon-color - The color of the clear icon. (default: var(--dds-button-subtle-icon-neutral-enabled))
 * @cssproperty --dds-combobox-popup-background - The background color of the popup. (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-combobox-popup-shadow - The box shadow of the popup. (default: 0 4px 6px -1px rgb(0 0 0 / 8%), 0 2px 4px -1px rgb(0 0 0 / 6%))
 * @cssproperty --dds-combobox-padding-xs - The padding for extra small size. (default: var(--dds-spacing-200))
 * @cssproperty --dds-combobox-padding-sm - The padding for small size. (default: var(--dds-spacing-200))
 * @cssproperty --dds-combobox-padding-lg - The padding for large size. (default: var(--dds-spacing-300))
 * @cssproperty --dds-combobox-padding-horizontal - The horizontal padding. (default: var(--dds-spacing-300))
 * @cssproperty --dds-combobox-padding-vertical - The vertical padding. (default: var(--dds-spacing-200))
 * @cssproperty --dds-combobox-gap - The gap between elements. (default: var(--dds-spacing-100))
 * @cssproperty --dds-combobox-icon-gap - The gap between icons. (default: var(--dds-spacing-200))
 * @cssproperty --dds-combobox-action-gap - The gap between action elements. (default: var(--dds-spacing-200))
 * @cssproperty --dds-combobox-action-padding - The padding for action elements. (default: var(--dds-spacing-300))
 * @cssproperty --dds-combobox-clear-icon-width - The width of the clear icon. (default: var(--dds-spacing-800))
 * @cssproperty --dds-combobox-dropdown-icon-right - The right position of the dropdown icon. (default: var(--dds-spacing-600))
 * @cssproperty --dds-combobox-min-width - The minimum width of the combobox. (default: 7.5rem)
 */
export default class DapDSDatePicker extends GenericFormElement {
  static tagName = 'dap-ds-datepicker'

  static dependencies = {
    'dap-ds-form-label': DapDSFormLabel,
    'dap-ds-tooltip': DapDSTooltip,
    'dap-ds-icon-button': DapDSIconButton,
    'dap-ds-icon': DapDSIcon,
    'dap-ds-typography': DapDSTypography,
    'dap-ds-popup': DapDSPopup,
    'dap-ds-icon-loading-spinner': SystemLoadingSpinner,
    'dap-ds-icon-calendar-line': BusinessCalendarLine,
    'dap-ds-calendar': DapDSCalendar,
    'dap-ds-feedback': DapDSFeedback,
  }

  private static nextId = 0

  /**
   * Property converter for date properties.
   * Converts string attributes (from React) to Dayjs objects.
   * Allows both attribute binding (strings) and property binding (Dayjs objects).
   *
   * Note: When properties are set directly (bypassing attributes), they are normalized
   * in the `updated` lifecycle method via `normalizeDate`.
   */
  private static datePropertyConverter = {
    fromAttribute: (value: string | null): Dayjs | undefined => {
      if (value === null || value === undefined || value === '') {
        return undefined
      }
      const trimmed = value.trim()
      if (!trimmed) return undefined

      // Check if it's a numeric timestamp string
      if (/^\d+$/.test(trimmed)) {
        const numValue = Number(trimmed)
        // Check if it's seconds (typically 10 digits, less than 10000000000)
        if (numValue > 0 && numValue < 10000000000) {
          return dayjs(numValue * 1000)
        }
        // Check if it's a valid timestamp (milliseconds since epoch)
        if (numValue >= 0 && numValue <= 4102444800000) {
          return dayjs(numValue)
        }
      }
      // Parse as date string (ISO string, date string, etc.)
      return dayjs(value)
    },
    toAttribute: (
      value: Dayjs | string | Date | number | undefined | null,
    ): string | null => {
      if (value === undefined || value === null) {
        return null // null removes the attribute
      }
      // Convert Dayjs to ISO string for attribute
      if (dayjs.isDayjs(value)) {
        return value.toISOString()
      }
      if (value instanceof Date) {
        return value.toISOString()
      }
      if (typeof value === 'string') {
        return value
      }
      if (typeof value === 'number') {
        return String(value)
      }
      return null
    },
  }

  private readonly inputId: string
  private readonly labelId: string
  private readonly descriptionId: string

  @query('.combobox__input') private readonly triggerInput!: HTMLInputElement
  @query('#calendar') private readonly calendar!: DapDSCalendar
  @query('dap-ds-popup') private readonly popup!: HTMLElement

  /** The value of the datepicker (selected date in single mode).
   * @type {Dayjs}
   */
  @property({
    converter: DapDSDatePicker.datePropertyConverter,
  })
  override value?: Dayjs

  /** The mode of the datepicker - single date or range selection.
   * @type {'single' | 'range'}
   * @default 'single'
   */
  @property({ reflect: true }) mode: 'single' | 'range' = 'single'

  /** The current visible date (month and year) of the calendar. Only the month and year are considered.
   * @type {Dayjs | string | Date}
   * @default dayjs()
   */
  @property({
    converter: DapDSDatePicker.datePropertyConverter,
  })
  currentDate?: Dayjs | string | Date

  /** The minimum date (month and year) of the datepicker. Only the month and year are considered.
   * @type {Dayjs | string | Date}
   * @default dayjs('1901-01-01')
   */
  @property({
    converter: DapDSDatePicker.datePropertyConverter,
  })
  minDate?: Dayjs | string | Date

  /** The maximum date (month and year) of the datepicker. Only the month and year are considered.
   * @type {Dayjs | string | Date}
   * @default dayjs('2099-12-31')
   */
  @property({
    converter: DapDSDatePicker.datePropertyConverter,
  })
  maxDate?: Dayjs | string | Date

  /** The disabled date of the datepicker. This function will be called for each date to determine if it is disabled.
   * @type {(date: Dayjs) => boolean}
   * @default (date: Dayjs) => false
   */
  @property({ attribute: false }) disabledDate = (date: Dayjs) => {
    return !date
  }

  /** The start date of the range selection (only used in range mode).
   * @type {Dayjs | string | Date}
   */
  @property({
    converter: DapDSDatePicker.datePropertyConverter,
  })
  rangeStart?: Dayjs | string | Date

  /** The end date of the range selection (only used in range mode).
   * @type {Dayjs | string | Date}
   */
  @property({
    converter: DapDSDatePicker.datePropertyConverter,
  })
  rangeEnd?: Dayjs | string | Date

  /** The placement of the dropdown of the datepicker.
   * @type {'top-start' | 'top' | 'top-end' | 'right-start' | 'right' | 'right-end' | 'bottom-start' | 'bottom' | 'bottom-end' | 'left-start' | 'left' | 'left-end'}
   */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'

  /** The open state of the datepicker. */
  @property({ type: Boolean, reflect: true }) opened = false

  /** Wheter the calendar should open on typing. */
  @property({ type: Boolean }) openCalendarOnInput = false

  /** Wheter the calendar should send an empty event on typing. */
  @property({ type: Boolean }) sendEmptyEventOnInput = false

  /** The placeholder of the datepicker. */
  @property() placeholder: string | null

  /** The loading state of the datepicker. */
  @property({ type: Boolean }) loading?: boolean = false

  /** Whether to enable input masking. When active, only digit characters are accepted and format separators are auto-inserted after each complete digit group. Only works in single mode with a single configured format. */
  @property({ type: Boolean }) mask = false

  /** The format of the datepicker. This is a comma separated list of [formats](https://day.js.org/docs/en/display/format). */
  @property({ type: String }) format?: string

  /** The format separator character in the format attribute. */
  @property({ type: String }) formatSeparator: string = '|'

  /** Whether the clear button should be shown. */
  @property({ type: String }) clearButton?: string = 'true'

  /** The aria label of the clear button. */
  @property({ type: String }) clearButtonAriaLabel?: string

  /** `data-testid` for the clear button. */
  @property() clearButtonTestId = 'datepicker-clear-button'

  /** `data-testid` for the calendar indicator button that opens the calendar. */
  @property() calendarButtonTestId = 'datepicker-calendar-button'

  /** The floating strategy of the datepicker. */
  @property({ type: String }) floatingStrategy: FloatingStrategy = 'fixed'

  /** Whether to keep popup open when trigger leaves viewport. */
  @property({ type: Boolean }) disableViewportAutoClose: boolean = false

  /** The locale of the datepicker. By default it uses the global locale from dayjs. It is determined from the browser language.
   * @type {'hu' | 'en' | 'de'}
   */
  @property({ type: String }) locale: string = dayjs.locale()

  /** Whether to hide dates from adjacent months (previous/next) in the calendar grid. When not explicitly set, defaults to `false` for single mode and `true` for range mode.
   * @type {boolean}
   */
  @property({ type: Boolean }) hideAdjacentMonths?: boolean

  @state() private formatOptions: string[] = []
  @state() private setValueState: boolean = true
  private lastMaskDigitCount = 0

  static readonly styles = unsafeCSS(styles)

  constructor() {
    super()
    DapDSDatePicker.nextId++
    const uniqueSuffix =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : Date.now().toString(36)
    this.inputId = `input-${DapDSDatePicker.nextId}-${uniqueSuffix}`
    this.labelId = `label-${DapDSDatePicker.nextId}-${uniqueSuffix}`
    this.descriptionId = `description-${DapDSDatePicker.nextId}-${uniqueSuffix}`
    // Initialize the feedbackId from FeedbackMixin
    this.feedbackId = `feedback-${DapDSDatePicker.nextId}-${uniqueSuffix}`
  }

  /**
   * Normalizes a date value to a Dayjs object.
   * Handles Dayjs objects, timestamps (numbers or strings), strings, Date objects, and undefined/null.
   */
  private normalizeDate(
    value: Dayjs | string | Date | number | undefined | null,
  ): Dayjs | undefined {
    if (value === undefined || value === null) {
      return undefined
    }

    // If it's already a Dayjs object, return it
    if (dayjs.isDayjs(value)) {
      return value
    }

    // If it's a string, check if it's a numeric timestamp string
    if (typeof value === 'string') {
      // Check if the string is purely numeric (timestamp as string)
      const trimmed = value.trim()
      if (/^\d+$/.test(trimmed)) {
        const numValue = Number(trimmed)
        // Check if it's seconds (typically 10 digits, less than 10000000000)
        // Unix timestamps in seconds are typically 10 digits, milliseconds are 13 digits
        if (numValue > 0 && numValue < 10000000000) {
          return dayjs(numValue * 1000)
        }
        // Check if it's a valid timestamp (milliseconds since epoch)
        // Valid range: between 0 and year 2100 (4102444800000 ms)
        if (numValue >= 0 && numValue <= 4102444800000) {
          return dayjs(numValue)
        }
      }
      // If it's not a numeric string, let dayjs parse it as a date string
      return dayjs(value)
    }

    // If it's a number (timestamp), convert it
    if (typeof value === 'number') {
      // Check if it's seconds (typically 10 digits, less than 10000000000)
      // Unix timestamps in seconds are typically 10 digits, milliseconds are 13 digits
      if (value > 0 && value < 10000000000) {
        return dayjs(value * 1000)
      }
      // Check if it's a valid timestamp (milliseconds since epoch)
      // Valid range: between 0 and year 2100 (4102444800000 ms)
      if (value >= 0 && value <= 4102444800000) {
        return dayjs(value)
      }
    }

    // For Date objects, dayjs will handle them
    return dayjs(value)
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    // Set formatOptions BEFORE using getFormattedValue()
    this.formatOptions = this.format?.split(this.formatSeparator) || []

    this.calendar.mode = this.mode

    if (this.mode === 'range') {
      if (this.rangeStart) {
        const normalized = this.normalizeDate(this.rangeStart)
        if (normalized) {
          this.calendar.rangeStart = normalized.locale(this.locale)
        }
      }
      if (this.rangeEnd) {
        const normalized = this.normalizeDate(this.rangeEnd)
        if (normalized) {
          this.calendar.rangeEnd = normalized.locale(this.locale)
        }
      }
    } else if (this.value) {
      const normalized = this.normalizeDate(this.value)
      if (normalized) {
        this.calendar.value = normalized.locale(this.locale)
        // Update the internal value to be a Dayjs object
        this.value = normalized
        // Set the input field value if setValueState is enabled
        if (this.setValueState && this.triggerInput) {
          this.triggerInput.value = this.getFormattedValue()
        }
      }
    }

    const normalizedCurrentDate = this.normalizeDate(this.currentDate)
    this.calendar.currentDate = normalizedCurrentDate || dayjs()

    const normalizedMinDate = this.normalizeDate(this.minDate)
    this.calendar.minDate = normalizedMinDate || dayjs('1901-01-01')
    if (normalizedMinDate) {
      this.minDate = normalizedMinDate
    }

    const normalizedMaxDate = this.normalizeDate(this.maxDate)
    this.calendar.maxDate = normalizedMaxDate || dayjs('2099-12-31')
    if (normalizedMaxDate) {
      this.maxDate = normalizedMaxDate
    }
    this.calendar.disabledDate = this.disabledDate

    // Only set hideAdjacentMonths if explicitly provided
    if (this.hideAdjacentMonths !== undefined) {
      this.calendar.hideAdjacentMonths = this.hideAdjacentMonths
    }
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    // Process value changes - check both Lit's change detection AND if value exists but wasn't detected
    // This handles cases where React sets a dayjs object but Lit doesn't detect it as changed
    const valueChanged = _changedProperties.has('value')
    const valueExistsButNotProcessed =
      !valueChanged && this.value && this.mode === 'single' && this.hasUpdated

    if (
      (valueChanged || valueExistsButNotProcessed) &&
      this.mode === 'single'
    ) {
      const normalized = this.normalizeDate(this.value)
      if (normalized) {
        // Only update if the normalized value is different from current calendar value to avoid infinite loops
        const currentCalendarValue = this.calendar.value
        if (
          !currentCalendarValue ||
          !normalized.isSame(currentCalendarValue, 'day')
        ) {
          this.calendar.value = normalized.locale(this.locale)
          // Update the internal value to be a Dayjs object
          this.value = normalized
        }
      } else {
        this.calendar.value = undefined
      }
      if (this.setValueState && this.triggerInput) {
        this.triggerInput.value = this.getFormattedValue()
      }
      this.setValidity()
    }

    if (_changedProperties.has('rangeStart') && this.mode === 'range') {
      const normalized = this.normalizeDate(this.rangeStart)
      if (normalized) {
        this.calendar.rangeStart = normalized.locale(this.locale)
        this.rangeStart = normalized
      } else {
        this.calendar.rangeStart = undefined
      }
      if (this.setValueState && this.rangeStart && this.rangeEnd) {
        this.triggerInput.value = this.getFormattedValue()
      }
    }

    if (_changedProperties.has('rangeEnd') && this.mode === 'range') {
      const normalized = this.normalizeDate(this.rangeEnd)
      if (normalized) {
        this.calendar.rangeEnd = normalized.locale(this.locale)
        this.rangeEnd = normalized
      } else {
        this.calendar.rangeEnd = undefined
      }
      if (this.setValueState && this.rangeStart && this.rangeEnd) {
        this.triggerInput.value = this.getFormattedValue()
      }
    }

    if (_changedProperties.has('currentDate')) {
      const normalized = this.normalizeDate(this.currentDate)
      this.calendar.currentDate = normalized || dayjs()
    }

    if (_changedProperties.has('minDate')) {
      const normalized = this.normalizeDate(this.minDate)
      this.calendar.minDate = normalized || dayjs('1901-01-01')
      if (normalized) {
        this.minDate = normalized
      }
    }

    if (_changedProperties.has('maxDate')) {
      const normalized = this.normalizeDate(this.maxDate)
      this.calendar.maxDate = normalized || dayjs('2099-12-31')
      if (normalized) {
        this.maxDate = normalized
      }
    }

    if (_changedProperties.has('disabledDate')) {
      this.calendar.disabledDate = this.disabledDate
    }

    if (
      _changedProperties.has('format') ||
      _changedProperties.has('formatSeparator')
    ) {
      this.formatOptions = this.format?.split(this.formatSeparator) || []
    }
  }

  getFormattedValue() {
    if (this.mode === 'range') {
      if (!this.rangeStart || !this.rangeEnd) {
        return ''
      }
      const format = this.formatOptions[0] || 'L'
      const startStr = dayjs(this.rangeStart).locale(this.locale).format(format)
      const endStr = dayjs(this.rangeEnd).locale(this.locale).format(format)
      return `${startStr} - ${endStr}`
    }

    if (!this.value) {
      return ''
    }

    return dayjs(this.value)
      .locale(this.locale)
      .format(this.formatOptions[0] || 'L')
  }

  isValidFormat(value: string) {
    if (!this.formatOptions.length) {
      // For the default 'L' format, we need to be more strict to prevent partial dates
      // Get the actual format pattern for the current locale
      const trimmedValue = value.trim()

      // Get the actual 'L' format pattern for the current locale
      const localeFormat = dayjs()
        .locale(this.locale)
        .localeData()
        .longDateFormat('L')

      // Now validate that the input actually matches the expected format pattern
      // Use the robust string comparison approach: parse and format back to compare
      // The localeFormat already contains the correct format for the current locale
      const parsedDate = dayjs(trimmedValue, localeFormat)
      if (!parsedDate.isValid()) {
        return false
      }
      // Format back and compare - if they differ, the input was incomplete
      // Handle optional trailing separators by normalizing both strings
      const formattedDate = parsedDate.format(localeFormat)
      const normalizedInput = trimmedValue.replace(/[.,\s]+$/, '') // Remove trailing separators
      const normalizedFormatted = formattedDate.replace(/[.,\s]+$/, '') // Remove trailing separators
      if (normalizedFormatted !== normalizedInput) {
        return false
      }

      return true
    }

    return this.formatOptions.some(format => {
      // Use the same robust string comparison approach for custom formats
      // Parse with the custom format and compare the result
      const parsedDate = dayjs(value, format)
      if (!parsedDate.isValid()) {
        return false
      }
      // Format back and compare - if they differ, the input was incomplete
      // Handle optional trailing separators by normalizing both strings
      const formattedDate = parsedDate.format(format)
      const normalizedInput = value.replace(/[.,\s]+$/, '') // Remove trailing separators
      const normalizedFormatted = formattedDate.replace(/[.,\s]+$/, '') // Remove trailing separators
      return normalizedFormatted === normalizedInput
    })
  }

  isValueValid() {
    if (!this.value) {
      return true
    }

    return this.isInputValid()
  }

  isInputValid() {
    return this.isValidFormat(this.triggerInput.value)
  }

  setValue(
    value?: Dayjs,
    setInputValue: boolean = true,
    sendEmptyEventOnInput: boolean = true,
  ) {
    this.value = value

    if (setInputValue) {
      this.setValueState = true
      this.triggerInput.value = this.getFormattedValue()
    } else {
      this.setValueState = false
    }

    // Update form value for form participation
    if (this.internals) {
      this.internals.setFormValue(
        this.value ? this.value.toString() : this.defaultValue,
      )
    }

    this.setValidity()
    this.hideCalendar()

    if (sendEmptyEventOnInput) {
      this.emit('dds-change', { value: this.value })
      this.emit('dds-valid-date', { value: this.triggerInput.value })
    }
  }

  async clearSelection() {
    this.lastMaskDigitCount = 0
    this.setValue(undefined, true, true)
  }

  private isMaskActive(): boolean {
    return this.mask && this.mode === 'single' && this.formatOptions.length <= 1
  }

  private getActiveMaskFormat(): string {
    if (this.formatOptions.length > 0) {
      return this.formatOptions[0]
    }
    return dayjs().locale(this.locale).localeData().longDateFormat('L')
  }

  private parseMaskTokens(format: string): MaskToken[] {
    const tokens: MaskToken[] = []
    let i = 0
    while (i < format.length) {
      if (format.startsWith('YYYY', i)) {
        tokens.push({ type: 'digit', length: 4 })
        i += 4
      } else if (format.startsWith('YY', i)) {
        tokens.push({ type: 'digit', length: 2 })
        i += 2
      } else if (format.startsWith('MM', i)) {
        tokens.push({ type: 'digit', length: 2 })
        i += 2
      } else if (format.startsWith('DD', i)) {
        tokens.push({ type: 'digit', length: 2 })
        i += 2
      } else if (format[i] === 'M') {
        tokens.push({ type: 'digit', length: 2 })
        i += 1
      } else if (format[i] === 'D') {
        tokens.push({ type: 'digit', length: 2 })
        i += 1
      } else {
        tokens.push({ type: 'separator', value: format[i] })
        i += 1
      }
    }
    return tokens
  }

  private buildMaskedValue(digits: string): string {
    const tokens = this.parseMaskTokens(this.getActiveMaskFormat())
    let result = ''
    let digitIndex = 0

    for (const token of tokens) {
      if (token.type === 'separator') {
        if (digitIndex > 0) {
          result += token.value
        }
      } else {
        const available = digits.slice(digitIndex, digitIndex + token.length!)
        result += available
        digitIndex += available.length
        if (available.length < token.length!) {
          break
        }
      }
    }

    return result
  }

  private readonly handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (
      this &&
      !path.includes(this) &&
      (!this.popup || !path.includes(this.popup))
    ) {
      this.hideCalendar()
    }
  }

  private readonly handlePopupAutoClose = () => {
    this.hideCalendar({ skipFocus: true })
  }

  private readonly handleInput = (event: InputEvent) => {
    const input = event.target as HTMLInputElement

    if (this.isMaskActive()) {
      let rawDigits = input.value.replace(/\D/g, '')
      const isDeleting =
        event.inputType === 'deleteContentBackward' ||
        event.inputType === 'deleteContentForward'
      if (isDeleting && rawDigits.length === this.lastMaskDigitCount) {
        rawDigits = rawDigits.slice(0, -1)
      }
      this.lastMaskDigitCount = rawDigits.length
      const masked = this.buildMaskedValue(rawDigits)
      input.value = masked
      input.setSelectionRange(masked.length, masked.length)
    }

    if (!input.value) {
      this.setValue(undefined, false, true)
    } else {
      if (this.isInputValid()) {
        const date = dayjs(input.value).locale(this.locale)

        // Normalize minDate and maxDate to ensure they're Dayjs objects
        const minDate = this.normalizeDate(this.minDate) || dayjs('1901-01-01')
        const maxDate = this.normalizeDate(this.maxDate) || dayjs('2099-12-31')

        if (date.isBefore(minDate, 'day') || date.isAfter(maxDate, 'day')) {
          this.setValue(undefined, false, this.sendEmptyEventOnInput)
          this.emit('dds-invalid-date', {
            value: input.value,
            type: 'out-of-range',
          })
        } else {
          this.setValue(date, false)
          this.currentDate = date
          this.emit('dds-valid-date', { value: input.value })
        }
      } else {
        this.setValue(undefined, false, this.sendEmptyEventOnInput)
        this.emit('dds-invalid-date', { value: input.value, type: 'invalid' })
      }
    }

    if (this.openCalendarOnInput && !this.opened) {
      this.showCalendar()
    }

    this.emit('dds-input', { value: input.value })
  }

  private readonly handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      return
    }

    if (
      event.key === 'Enter' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp'
    ) {
      event.preventDefault()
      event.stopPropagation()

      if (!this.opened) {
        this.showCalendar()
        await this.updateComplete
        this.calendar.captureFocus()
      } else {
        this.hideCalendar()
      }
    }
  }

  public get focusElement() {
    return this.triggerInput
  }

  private readonly handleClick = () => {
    if (!this.opened) {
      this.showCalendar()
    } else {
      this.hideCalendar()
    }
  }

  private readonly handleClearClick = () => {
    if (!this.disabled) {
      this.clearSelection()

      this.triggerInput.focus()
      this.setValidity()

      this.hideCalendar()

      this.emit('dds-clear')
    }
  }

  async showCalendar() {
    if (this.disabled || this.readonly || this.opened) {
      return
    }

    this.opened = true

    document.addEventListener('mousedown', this.handleDocumentMouseDown)
  }

  async hideCalendar(options?: { skipFocus?: boolean }) {
    if (!this.opened) {
      return
    }

    this.opened = false

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    if (!options?.skipFocus) {
      this.focus()
    }
  }

  private readonly handleFocus = () => {
    this.emit('dds-focus')
  }

  private readonly handleBlur = () => {
    this.emit('dds-blur')
  }

  // custom validation
  getValidity() {
    return !!this.value
  }

  get validity(): ValidityState {
    if (this.internals.validity) {
      return this.internals.validity
    } else {
      return getValidityStateObject({
        valueMissing: this.required && !this.getValidity(),
        patternMismatch: !this.isValueValid(),
      })
    }
  }

  checkValidity() {
    return this.required && this.getValidity() && this.isValueValid()
  }

  reportValidity() {
    return this.required && this.getValidity() && this.isValueValid()
  }

  setValidity() {
    if (!this.checkValidity()) {
      this.internals.setValidity(
        {
          valueMissing: this.required && !this.getValidity(),
          patternMismatch: !this.isValueValid(),
        },
        this.validationMessage,
        this.triggerInput,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  render() {
    return html` <div class="label-container">
        ${this.label &&
        html`<dap-ds-form-label
          part="label"
          id=${this.labelId}
          .size=${this.size}
          .required=${this.required}
          .requiredLabel=${this.requiredLabel}
          .optional=${this.optional}
          .optionalLabel=${this.optionalLabel}
          .disabled=${this.disabled}
          class=${clsx('label')}
          .noMargin=${!!this.description}
          for=${this.inputId}
          .hideLabel=${this.hideLabel}
          label=${ifDefined(this.label)}>
        </dap-ds-form-label>`}
        ${this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button
            slot="trigger"
            size=${this.size as 'xs' | 'sm' | 'md' | 'lg'}
            iconSize=${this.size === 'xs' ? '16' : '22'}
            class="info-button"
            clean>
            <dap-ds-icon
              name="information-line"
              aria-label=${this.tooltipAriaLabel}
              class="info-button"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`}
      </div>
      ${this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          class=${clsx('input-description')}
          size=${ifDefined(this.size as CommonFormElementSize)}
          id=${this.descriptionId}>
          ${this.description}
        </dap-ds-typography>
      `}
      <div>
        <dap-ds-popup
          part="base"
          class="select-popup"
          overflow
          ?opened=${this.opened}
          ?hasArrow=${false}
          placement=${this.placement}
          floatingStrategy=${this.floatingStrategy}
          maxHeight="auto"
          offset="4"
          ?disableViewportAutoClose=${this.disableViewportAutoClose}
          @dds-close=${this.handlePopupAutoClose}>
          <div
            slot="trigger"
            part="trigger"
            class="${clsx('select', `select--${this.size}`, {
              'select--disabled': this.disabled,
              'select--readonly': this.readonly,
              'select--opened': this.opened,
              'select--loading': this.loading,
              'select--success': this.status === 'success',
              'select--error': this.status === 'error',
              'select--no-animation': true,
            })}">
            <input
              part="input"
              role="combobox"
              class="${clsx('combobox__input', 'input', `input--${this.size}`, {
                'input--disabled': this.disabled,
                'input--readonly': this.readonly,
              })}"
              id=${this.inputId}
              value=${this.getFormattedValue()}
              aria-expanded=${this.opened}
              aria-haspopup="dialog"
              aria-labelledby=${ifDefined(this.label ? this.labelId : nothing)}
              aria-describedby="${ifDefined(
                this.description ? this.descriptionId : nothing,
              )} ${ifDefined(this.feedback ? this.feedbackId : nothing)}"
              aria-disabled=${this.disabled}
              aria-controls="calendar"
              aria-autocomplete="none"
              ?required=${this.required}
              ?readonly=${this.readonly}
              ?autofocus=${this.autofocus}
              ?disabled=${this.disabled}
              autocomplete=${this.autocomplete as any}
              placeholder=${ifDefined(this.placeholder ?? '')}
              @blur=${this.handleBlur}
              @focus=${this.handleFocus}
              @input=${this.handleInput}
              @keydown=${this.handleKeyDown}
              @invalid=${this.handleInvalid}
              @click=${this.handleClick} />
            <div
              class="${clsx('select-action', `select-action--${this.size}`, {
                'select-action--disabled': this.disabled,
                'select-action--readonly': this.readonly,
                'select-action--opened': this.opened,
              })}">
              ${this.clearButton === 'true' && !this.readonly
                ? html`<div class="${clsx('clear-icon')}">
                    <dap-ds-icon-button
                      icon="close-line"
                      part="clear-button"
                      data-testid=${this.clearButtonTestId}
                      aria-label=${ifDefined(
                        this.clearButtonAriaLabel ??
                          t('clear', { lng: this.locale }),
                      )}
                      ?disabled=${this.disabled}
                      @click=${this.handleClearClick}></dap-ds-icon-button>
                  </div>`
                : nothing}
              <div
                class="${clsx('indicator-icon', {
                  'indicator-icon--loading': this.loading,
                })}">
                ${this.loading
                  ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
                  : html`<dap-ds-icon-calendar-line
                      aria-hidden="true"
                      part="calendar-button"
                      data-testid=${this.calendarButtonTestId}
                      class=${clsx('calendar-icon')}
                      @click=${this.handleClick}></dap-ds-icon-calendar-line>`}
              </div>
            </div>
          </div>
          <dap-ds-calendar
            id="calendar"
            part="calendar"
            exportparts="calendar-header-button-prev, calendar-header-button-next, calendar-header-year-select, calendar-header-month-select, calendar-grid-header-cell, calendar-grid-cell, calendar-grid-header-cell-base, calendar-grid-cell-base, calendar-grid-cell-today-mark"
            locale=${this.locale}
            @dds-close=${this.hideCalendar}
            @dds-change=${(event: CustomEvent) => {
              event.preventDefault()
              event.stopPropagation()
              if (this.mode === 'single') {
                this.setValue(event.detail.value)
              }
            }}
            @dds-range-change=${(event: CustomEvent) => {
              event.preventDefault()
              event.stopPropagation()
              if (this.mode === 'range') {
                this.rangeStart = event.detail.startDate
                this.rangeEnd = event.detail.endDate
                this.triggerInput.value = this.getFormattedValue()
                this.emit('dds-change', {
                  startDate: this.rangeStart,
                  endDate: this.rangeEnd,
                })
                this.hideCalendar()
              }
            }}>
          </dap-ds-calendar>
        </dap-ds-popup>
        ${this.feedback &&
        html`<dap-ds-feedback
          part="feedback"
          feedbackType=${ifDefined(this.feedbackType)}
          feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
          id="feedback">
          ${this.feedback}
        </dap-ds-feedback>`}
      </div>`
  }
}
